import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ReassuranceSection from '@/components/feature/ReassuranceSection';
import { SchemaLocalBusiness, SchemaBreadcrumb } from '@/components/feature/SchemaOrg';

const FORM_URL = 'https://formspree.io/f/meewdjbl';

export default function PresupuestoPage() {
  const location = useLocation();
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    direccion: '',
    tipoServicio: '',
    descripcion: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.descripcion.length > 500) {
      setError('La descripción no puede superar los 500 caracteres.');
      return;
    }

    setLoading(true);
    try {
      const body = new URLSearchParams({
        nombre: formData.nombre,
        telefono: formData.telefono,
        email: formData.email,
        direccion: formData.direccion,
        tipoServicio: formData.tipoServicio,
        descripcion: formData.descripcion,
        _subject: 'Nuevo presupuesto — Toldos Málaga',
        formulario: 'Presupuesto',
      });

      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        body,
      });
      setSubmitted(true);
    } catch (err) {
      setError('Error al enviar el formulario. Inténtalo de nuevo o llámanos por teléfono.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <title>Solicitar Presupuesto de Toldos en Málaga | Presupuesto Gratis | ToldosMálaga</title>
      <meta name="description" content="Solicita tu presupuesto gratis para reparación, instalación o mantenimiento de toldos en Málaga. Respuesta en 24 horas. Sin compromiso. Teléfono 951 12 07 70." />
      <meta name="keywords" content="presupuesto toldos Málaga, presupuesto gratis toldos, reparación toldos presupuesto, instalar toldo precio Málaga" />
      <link rel="canonical" href="https://toldos-malaga.es/presupuesto" />

      <SchemaLocalBusiness />
      <SchemaBreadcrumb
        items={[
          { name: 'Inicio', url: 'https://toldos-malaga.es/' },
          { name: 'Solicitar presupuesto', url: 'https://toldos-malaga.es/presupuesto' },
        ]}
      />
      <meta property="og:title" content="Solicitar Presupuesto de Toldos en Málaga | Presupuesto Gratis | Toldos Málaga" />
      <meta property="og:description" content="Solicita tu presupuesto gratis para reparación, instalación o mantenimiento de toldos en Málaga. Respuesta en 24 horas. Sin compromiso. Teléfono 951 12 07 70." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://toldos-malaga.es/presupuesto" />
      <meta property="og:image" content="https://toldos-malaga.es/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Toldos Málaga" />
      <meta property="og:locale" content="es_ES" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Presupuesto Gratis de Toldos en Málaga | Toldos Málaga" />
      <meta name="twitter:description" content="Solicita presupuesto gratis sin compromiso. Reparación, instalación y mantenimiento de toldos. Respuesta 24h." />
      <meta name="twitter:image" content="https://toldos-malaga.es/og-image.jpg" />

      <Navbar />
      <main className="min-h-screen bg-background-50">
        <nav aria-label="Breadcrumb" className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-28 pb-2">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-500 transition-colors duration-200">Inicio</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-700 font-medium">Solicitar presupuesto</li>
          </ol>
        </nav>

        <ReassuranceSection />

        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Form Column */}
            <div className="lg:col-span-3">
              <header className="mb-8">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-3">
                  Solicita tu presupuesto <span className="text-primary-500">sin compromiso</span>
                </h1>
                <p className="text-base text-foreground-600 leading-relaxed">
                  Cuéntanos qué necesitas y te enviamos un presupuesto detallado en menos de <strong>24 horas</strong>. Sin compromiso ni coste alguno.
                </p>
              </header>

              {submitted ? (
                <div className="bg-accent-100/80 border border-accent-300/60 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-accent-500/20 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-accent-600 text-3xl"></i>
                  </div>
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">¡Solicitud enviada!</h2>
                  <p className="text-foreground-600 mb-4">
                    Hemos recibido tu solicitud correctamente. Te responderemos en menos de 24 horas con un presupuesto personalizado.
                  </p>
                  <a
                    href="tel:+34951120770"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-phone-line text-lg"></i>
                    <span>¿Urgente? Llámanos: 951 12 07 70</span>
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form="presupuesto-toldos-malaga">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Nombre completo <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        required
                        value={formData.nombre}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="Tu nombre y apellidos"
                      />
                    </div>
                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Teléfono <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        required
                        value={formData.telefono}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="600 000 000"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Correo electrónico
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="tu@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="direccion" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Dirección donde instalar/reparar
                    </label>
                    <input
                      type="text"
                      id="direccion"
                      name="direccion"
                      value={formData.direccion}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="Calle, número, código postal, Málaga"
                    />
                  </div>
                  <div>
                    <label htmlFor="tipoServicio" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Tipo de servicio <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="tipoServicio"
                      name="tipoServicio"
                      required
                      value={formData.tipoServicio}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    >
                      <option value="">Selecciona una opción</option>
                      <option value="reparacion">Reparación de toldo</option>
                      <option value="instalacion">Instalación de toldo nuevo</option>
                      <option value="motorizacion">Motorización</option>
                      <option value="mantenimiento">Mantenimiento / Revisión</option>
                      <option value="hosteleria">Toldo para hostelería / negocio</option>
                      <option value="otro">Otro / No estoy seguro</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="descripcion" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Describe lo que necesitas <span className="text-red-500">*</span>
                      <span className="text-foreground-400 font-normal ml-2">({formData.descripcion.length}/500)</span>
                    </label>
                    <textarea
                      id="descripcion"
                      name="descripcion"
                      required
                      maxLength={500}
                      rows={4}
                      value={formData.descripcion}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="Cuéntanos qué le pasa a tu toldo, tipo de toldo, dimensiones aproximadas si las conoces, urgencia..."
                    />
                  </div>

                  {error && (
                    <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 whitespace-nowrap"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line text-lg animate-spin"></i>
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <span>Solicitar presupuesto gratis</span>
                        <i className="ri-arrow-right-line text-lg"></i>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-[#0F5066] rounded-2xl p-6 text-white">
                <h2 className="text-lg font-heading font-bold text-white mb-3">¿Prefieres llamar?</h2>
                <p className="text-white/70 text-sm leading-relaxed mb-4">
                  Si tu toldo necesita una reparación urgente, llámanos directamente. Atendemos de lunes a sábado.
                </p>
                <div className="space-y-3">
                  <a
                    href="tel:+34951120770"
                    className="flex items-center gap-3 px-5 py-3.5 bg-white text-[#0F5066] rounded-full font-bold text-sm hover:bg-accent-100 transition-all duration-200 whitespace-nowrap"
                  >
                    <i className="ri-phone-line text-xl"></i>
                    <span>951 12 07 70</span>
                  </a>
                  <a
                    href="https://wa.me/34627213347"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-5 py-3.5 bg-accent-500 text-foreground-900 rounded-full font-bold text-sm hover:bg-accent-400 transition-all duration-200 whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    <span>627 21 33 47</span>
                  </a>
                </div>
              </div>

              <div className="bg-secondary-100/80 border border-secondary-200/60 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3">Información útil</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2.5 text-sm text-foreground-600">
                    <i className="ri-map-pin-line text-primary-500 mt-0.5"></i>
                    <span><strong className="text-foreground-800">Dirección:</strong> Calle Pacífico 54, San Andrés, 29003 Málaga</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-foreground-600">
                    <i className="ri-time-line text-primary-500 mt-0.5"></i>
                    <span><strong className="text-foreground-800">Horario:</strong> L-V 9:00-18:00, S 10:00-14:00</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-foreground-600">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-0.5"></i>
                    <span><strong className="text-foreground-800">Respuesta:</strong> 24 horas (días laborables)</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-foreground-600">
                    <i className="ri-shield-check-line text-primary-500 mt-0.5"></i>
                    <span><strong className="text-foreground-800">Garantía:</strong> 2 años en reparaciones e instalaciones</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}